/**
 * 004 — Configuración por estilo (paleta, tipografía, ornamentos) extraída fielmente
 * de cada plantilla de referencia del DS (design-system/project/templates/boda-ESTILO/).
 * La anatomía es idéntica
 * en los 7 (15 secciones); solo cambian estos valores. Cada estilo tiene su archivo
 * delgado (independiente) que consume `BodaTemplate` con su config; si un estilo
 * necesita divergir estructuralmente, su archivo puede "eyectarse" a un port propio.
 */
import type { EstiloId } from '@/invitation/payload/types';

export interface EstiloConfig {
  themeClass: string;
  /** La fuente de titulares: 'cursive' (Great Vibes/Parisienne) o 'display' (Cinzel/Italiana). */
  headingKind: 'cursive' | 'display';
  f: { script: string; serif: string; caps: string; body: string };
  c: {
    bg: string; ink: string;
    accent: string; accentSoft: string; accentLight: string;
    sec: string; muted: string;
    dark: string; onDark: string;
    regalosBg: string;
  };
  /** Filigrana por sección (undefined = sin filigrana en esa sección). */
  fil: { historia?: string; fecha?: string; familia?: string; notas?: string; confirmacion?: string };
}

const BASE_F = { serif: "'Cormorant Garamond', serif", caps: "'Tenor Sans', sans-serif", body: "'Jost', sans-serif" };
const FULL_FIL = { historia: 'guirnalda', fecha: 'laurel-corona', familia: 'rama-botanica', notas: 'anillos', confirmacion: 'arco' };
const MIN_FIL = { notas: 'anillos', confirmacion: 'arco' };

export const ESTILO_CONFIGS: Record<EstiloId, EstiloConfig> = {
  elegante: {
    themeClass: 'theme-elegante', headingKind: 'cursive',
    f: { script: "'Great Vibes', cursive", ...BASE_F },
    c: { bg: '#f8f2e9', ink: '#2e2620', accent: '#b08d4f', accentSoft: '#c9ab74', accentLight: '#d8bd87', sec: '#6f6157', muted: '#a3968a', dark: '#221913', onDark: '#f4e9d4', regalosBg: 'linear-gradient(180deg,#f3e7d3,#f8f2e9)' },
    fil: FULL_FIL,
  },
  romantico: {
    themeClass: 'theme-romantico', headingKind: 'cursive',
    f: { script: "'Parisienne', cursive", ...BASE_F },
    c: { bg: '#fdf3f0', ink: '#43272c', accent: '#c08a86', accentSoft: '#d6a9a4', accentLight: '#e8c9b8', sec: '#83626a', muted: '#b49aa0', dark: '#3d2226', onDark: '#fcf4f2', regalosBg: 'linear-gradient(180deg,#f3ddd8,#fdf3f0)' },
    fil: FULL_FIL,
  },
  epoca: {
    themeClass: 'theme-epoca', headingKind: 'display',
    f: { script: "'Cinzel', serif", ...BASE_F },
    c: { bg: '#f8f0dc', ink: '#143028', accent: '#a8874c', accentSoft: '#c1a469', accentLight: '#e8d3a6', sec: '#2f5346', muted: '#89a08f', dark: '#0a1a15', onDark: '#f2e7cf', regalosBg: 'linear-gradient(180deg,#f2e7cf,#f8f0dc)' },
    fil: { fecha: 'laurel-corona', notas: 'anillos', confirmacion: 'arco' },
  },
  rustico: {
    themeClass: 'theme-rustico', headingKind: 'cursive',
    f: { script: "'Parisienne', cursive", ...BASE_F },
    c: { bg: '#f5efe4', ink: '#372c21', accent: '#b96a49', accentSoft: '#cb8c6d', accentLight: '#e0b394', sec: '#6d5c4a', muted: '#a0917c', dark: '#33291f', onDark: '#f6efe2', regalosBg: 'linear-gradient(180deg,#eee0cc,#f5efe4)' },
    fil: FULL_FIL,
  },
  costero: {
    themeClass: 'theme-costero', headingKind: 'cursive',
    f: { script: "'Parisienne', cursive", ...BASE_F },
    c: { bg: '#f6f3ea', ink: '#17383c', accent: '#3f8f97', accentSoft: '#6cb0b4', accentLight: '#a8d3d2', sec: '#4c6b6d', muted: '#93a5a4', dark: '#0f3338', onDark: '#f2fbf8', regalosBg: 'linear-gradient(180deg,#e4efe6,#f6f3ea)' },
    fil: FULL_FIL,
  },
  urbano: {
    themeClass: 'theme-urbano', headingKind: 'display',
    f: { script: "'Italiana', serif", ...BASE_F },
    c: { bg: '#f4f2ef', ink: '#1d2023', accent: '#a5613c', accentSoft: '#bd7f5b', accentLight: '#d9a982', sec: '#555c62', muted: '#93999e', dark: '#17191b', onDark: '#eceae6', regalosBg: 'linear-gradient(180deg,#e2dfd9,#f4f2ef)' },
    fil: MIN_FIL,
  },
  moderno: {
    themeClass: 'theme-moderno', headingKind: 'display',
    f: { script: "'Italiana', serif", ...BASE_F },
    c: { bg: '#faf9f6', ink: '#232322', accent: '#d24a33', accentSoft: '#e0705a', accentLight: '#f0a795', sec: '#4c4c4a', muted: '#94948f', dark: '#0d0d0d', onDark: '#f2f1ed', regalosBg: 'linear-gradient(180deg,#e8e6e0,#faf9f6)' },
    fil: MIN_FIL,
  },
};
